import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import Logo from "@/components/Logo";

const BIENVENUE = "/media/bienvenue.jpg";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const { tr } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body =
        mode === "login" ? { email, password } : { name, email, password };
      const { data } = await api.post(url, body);
      setUser(data);
      navigate(location.state?.from || "/tableau-de-bord");
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-aqua/20 bg-abyss/70 px-4 py-3.5 text-sm text-foam placeholder:text-foam/35 outline-none transition-all duration-300 focus:border-aqua focus:shadow-[0_0_20px_rgba(56,189,248,0.18)]";

  return (
    <main className="grid min-h-screen lg:grid-cols-2 bg-abyss pt-[72px]" data-testid="auth-page">
      <div className="relative hidden lg:block">
        <img src={BIENVENUE} alt="Bienvenus dans la magie de l'eau — OJANZU" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,10,22,0.2)_0%,rgba(3,10,22,0.7)_100%)]" />
        <div className="absolute bottom-16 left-12 right-12">
          <p className="font-serif text-4xl italic leading-snug text-foam [text-shadow:0_1px_18px_rgba(3,10,22,0.6)]">
            « {tr("L'eau porte, le silence transforme.")} »
          </p>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-foam/50">
            {tr("École Française de Janzu")}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-16 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-10 flex justify-center"><Logo size={40} /></div>
          <h1 className="font-serif text-4xl font-light text-foam">
            {mode === "login" ? tr("Bon retour dans l'eau.") : tr("Entrer dans l'eau.")}
          </h1>
          <p className="mt-3 text-sm text-foam/60">
            {mode === "login"
              ? tr("Connectez-vous pour retrouver votre progression et vos sessions.")
              : tr("Créez votre compte membre pour réserver vos modules de formation.")}
          </p>

          <div className="mt-8 flex rounded-full border border-aqua/20 p-1" data-testid="auth-tabs">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 rounded-full py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${
                  mode === m ? "bg-aqua text-abyss" : "text-foam/60 hover:text-foam"
                }`}
              >
                {m === "login" ? tr("Connexion") : tr("Inscription")}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <input
                data-testid="register-name-input"
                type="text"
                required
                placeholder={tr("Votre nom")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls}
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              required
              placeholder={tr("Email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
            <input
              data-testid="auth-password-input"
              type="password"
              required
              minLength={8}
              placeholder={tr("Mot de passe (8 caractères min.)")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />

            {error && (
              <p className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300" data-testid="auth-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="auth-submit-button"
              disabled={busy}
              className="w-full rounded-full bg-aqua py-4 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam hover:shadow-[0_0_36px_rgba(56,189,248,0.5)] disabled:opacity-50"
            >
              {busy ? (
                <span className="inline-block h-4 w-4 rounded-full border-2 border-abyss/40 border-t-abyss animate-spin" />
              ) : mode === "login" ? (
                tr("Se connecter")
              ) : (
                tr("Créer mon compte")
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-foam/40">
            <Link to="/" data-testid="auth-back-link" className="hover:text-foam transition-colors">
              ← {tr("Retour au site")}
            </Link>
          </p>
        </motion.div>
      </div>
    </main>
  );
}
